import React from 'react';
import {
    BookOpen,
    Calendar,
    Award,
    Clock,
    FileText,
    TrendingUp,
    Target,
    CheckCircle,
    AlertCircle,
    Star,
    GraduationCap,
    Users,
    Bell,
    Activity
} from 'lucide-react';
import { Card } from '../../components/ui';

const DashboardStudent: React.FC = () => {
    const studentStats = [
        {
            name: 'My Subjects',
            value: '6',
            change: '+1',
            changeType: 'positive',
            icon: BookOpen,
            color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
            trend: 'up',
        },
        {
            name: 'Current GPA',
            value: '3.8',
            change: '+0.2',
            changeType: 'positive',
            icon: Award,
            color: 'bg-gradient-to-r from-teal-500 to-teal-600',
            trend: 'up',
        },
        {
            name: 'Assignments Due',
            value: '5',
            change: '-2',
            changeType: 'negative',
            icon: FileText,
            color: 'bg-gradient-to-r from-orange-500 to-orange-600',
            trend: 'down',
        },
        {
            name: 'Attendance Rate',
            value: '96%',
            change: '+1%',
            changeType: 'positive',
            icon: CheckCircle,
            color: 'bg-gradient-to-r from-cyan-500 to-cyan-600',
            trend: 'up',
        },
    ];

    const studentMetrics = [
        {
            name: 'Classes Today',
            value: '4',
            change: '2:00 PM',
            changeType: 'neutral',
            icon: Clock,
            trend: 'neutral',
        },
        {
            name: 'Completed Assignments',
            value: '12',
            change: '+3',
            changeType: 'positive',
            icon: CheckCircle,
            trend: 'up',
        },
        {
            name: 'Upcoming Exams',
            value: '2',
            change: 'Next week',
            changeType: 'neutral',
            icon: Target,
            trend: 'neutral',
        },
    ];

    const mySubjects = [
        { subject: 'Mathematics', teacher: 'Mr. Smith', grade: 'A', nextClass: 'Today 2:00 PM' },
        { subject: 'English', teacher: 'Ms. Johnson', grade: 'B+', nextClass: 'Today 3:30 PM' },
        { subject: 'Science', teacher: 'Dr. Brown', grade: 'A-', nextClass: 'Tomorrow 10:00 AM' },
        { subject: 'History', teacher: 'Mr. Wilson', grade: 'A', nextClass: 'Tomorrow 11:30 AM' },
        { subject: 'Physics', teacher: 'Dr. Davis', grade: 'B+', nextClass: 'Wednesday 9:00 AM' },
        { subject: 'Chemistry', teacher: 'Ms. Taylor', grade: 'A-', nextClass: 'Wednesday 1:00 PM' },
    ];

    const recentActivities = [
        {
            id: 1,
            type: 'assignment',
            message: 'Submitted Mathematics homework',
            time: '2 hours ago',
        },
        {
            id: 2,
            type: 'grade',
            message: 'Received grade for English essay',
            time: '1 day ago',
        },
        {
            id: 3,
            type: 'exam',
            message: 'Science mid-term exam scheduled',
            time: '2 days ago',
        },
        {
            id: 4,
            type: 'attendance',
            message: 'Attended all classes this week',
            time: '3 days ago',
        },
    ];

    const upcomingTasks = [
        { task: 'Complete Physics assignment', due: 'Tomorrow', priority: 'high' },
        { task: 'Study for History quiz', due: 'Friday', priority: 'medium' },
        { task: 'Prepare for Science exam', due: 'Next week', priority: 'high' },
        { task: 'Read English novel chapter', due: 'Next week', priority: 'low' },
    ];

    const gradeProgress = [
        { subject: 'Mathematics', current: 85, target: 90, color: 'bg-emerald-500' },
        { subject: 'English', current: 78, target: 85, color: 'bg-teal-500' },
        { subject: 'Science', current: 92, target: 90, color: 'bg-cyan-500' },
        { subject: 'History', current: 88, target: 85, color: 'bg-orange-500' },
        { subject: 'Physics', current: 82, target: 85, color: 'bg-purple-500' },
        { subject: 'Chemistry', current: 89, target: 90, color: 'bg-pink-500' },
    ];

    const announcements = [
        { title: 'Mid-term exams schedule released', time: '2 hours ago', type: 'exam' },
        { title: 'Library will be closed this weekend', time: '1 day ago', type: 'info' },
        { title: 'Sports day registration open', time: '2 days ago', type: 'event' },
        { title: 'Parent-teacher meeting next week', time: '3 days ago', type: 'meeting' },
    ];

    return (
        <div className="space-y-6">
            {/* Student Dashboard Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold gradient-text">Student Dashboard</h2>
                    <p className="text-gray-600 text-sm">Academic Progress Overview</p>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <GraduationCap className="h-3 w-3" />
                    <span>Student Portal</span>
                </div>
            </div>

            {/* Student Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {studentStats.map((stat) => (
                    <Card key={stat.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                                <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                                <div className="flex items-center space-x-1">
                                    {stat.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : stat.trend === 'down' ? (
                                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                                    ) : (
                                        <Target className="h-4 w-4 text-blue-500" />
                                    )}
                                    <p className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                                        stat.changeType === 'negative' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                        {stat.change} from last month
                                    </p>
                                </div>
                            </div>
                            <div className={`p-4 rounded-2xl ${stat.color} shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                                <stat.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Student Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {studentMetrics.map((metric) => (
                    <Card key={metric.name} glassy={true} className="islamic-pattern group hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-600 mb-1">{metric.name}</p>
                                <p className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
                                <div className="flex items-center space-x-1">
                                    {metric.trend === 'up' ? (
                                        <TrendingUp className="h-4 w-4 text-green-500" />
                                    ) : metric.trend === 'down' ? (
                                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                                    ) : (
                                        <Target className="h-4 w-4 text-blue-500" />
                                    )}
                                    <p className={`text-sm font-medium ${metric.changeType === 'positive' ? 'text-green-600' :
                                        metric.changeType === 'negative' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                        {metric.change}
                                    </p>
                                </div>
                            </div>
                            <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                                <metric.icon className="h-8 w-8 text-white" />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* My Subjects */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">My Subjects</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                            <BookOpen className="h-5 w-5 text-emerald-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {mySubjects.map((subject, index) => (
                            <div key={index} className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold text-gray-900">{subject.subject}</h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${subject.grade.startsWith('A') ? 'bg-green-100 text-green-700' :
                                        subject.grade.startsWith('B') ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                        {subject.grade}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-2">Teacher: {subject.teacher}</p>
                                <div className="flex items-center space-x-2">
                                    <Clock className="h-3 w-3 text-gray-500" />
                                    <span className="text-xs text-gray-500">Next: {subject.nextClass}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Grade Progress */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Grade Progress</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                            <Star className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {gradeProgress.map((progress, index) => (
                            <div key={index} className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{progress.subject}</span>
                                    <span className="text-sm font-bold text-gray-900">{progress.current}%</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="text-xs text-gray-600 w-16">Current</div>
                                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                                        <div
                                            className={`${progress.color} h-3 rounded-full`}
                                            style={{ width: `${progress.current}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs font-medium text-gray-600">Target: {progress.target}%</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Activities */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100">
                            <Activity className="h-5 w-5 text-orange-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {recentActivities.map((activity) => (
                            <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-shrink-0">
                                    <div className={`w-3 h-3 rounded-full mt-2 ${activity.type === 'assignment' ? 'bg-emerald-500' :
                                        activity.type === 'grade' ? 'bg-blue-500' :
                                            activity.type === 'exam' ? 'bg-orange-500' : 'bg-green-500'
                                        }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Upcoming Tasks */}
                <Card glassy={true} className="islamic-pattern">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h3>
                        <div className="p-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100">
                            <Target className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        {upcomingTasks.map((task, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/20 transition-colors duration-200">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{task.task}</p>
                                    <p className="text-xs text-gray-500">Due: {task.due}</p>
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>
                                    {task.priority}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Announcements */}
            <Card glassy={true} className="islamic-pattern">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">School Announcements</h3>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-blue-100 to-cyan-100">
                        <Bell className="h-5 w-5 text-blue-600" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {announcements.map((announcement, index) => (
                        <div key={index} className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm">
                            <div className="flex items-start justify-between mb-2">
                                <h4 className="text-sm font-semibold text-gray-900">{announcement.title}</h4>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${announcement.type === 'exam' ? 'bg-red-100 text-red-700' :
                                    announcement.type === 'event' ? 'bg-green-100 text-green-700' :
                                        announcement.type === 'meeting' ? 'bg-blue-100 text-blue-700' :
                                            'bg-gray-100 text-gray-700'
                                    }`}>
                                    {announcement.type}
                                </div>
                            </div>
                            <p className="text-xs text-gray-500">{announcement.time}</p>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Quick Actions */}
            <Card glassy={true} className="islamic-pattern">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-emerald-100 to-teal-100">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <a
                        href="/assignments"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <FileText className="h-6 w-6 text-emerald-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">View Assignments</p>
                    </a>
                    <a
                        href="/grades"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Award className="h-6 w-6 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">View Grades</p>
                    </a>
                    <a
                        href="/schedule"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <Calendar className="h-6 w-6 text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Class Schedule</p>
                    </a>
                    <a
                        href="/attendance"
                        className="p-4 border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 backdrop-blur-sm group"
                    >
                        <CheckCircle className="h-6 w-6 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
                        <p className="text-sm font-medium text-gray-900">Attendance</p>
                    </a>
                </div>
            </Card>
        </div>
    );
};

export default DashboardStudent;
